import React from 'react';
import { useForm } from 'antd/lib/form/Form';
import { TCar } from '../start-work-order/type';
import { Drawer, FormInstance, Image } from 'antd';
import { LicensePlateForm } from './components/license-plate';
import { AutoTrackButton } from '../../components/button';
import { CameraControl } from './components/camera-control';
import Webcam from 'react-webcam';
import { useScreenSize } from '../../hooks/useScreenSize';
import { FileUpload } from './components/FileUpload';
import { ICClose } from '../../icons';
import { useNotification } from '../../hooks/useNotification';
import { setLoading } from '../../redux/slices/appInfo';
import { useDispatch } from 'react-redux';
import { ApiInstance } from '../../api';
import { ScannerType } from './components/scanner-overlay';
import { SectionHeader } from './components/SectionHeader';
import styled from 'styled-components';

const DrawerBottom = styled(Drawer)`
    .ant-drawer-body {
        padding: 0;
    }
`;

type IFile = {
    url: string;
    name: string;
};

export type TAddReceiptsData = {
    carList: TCar[];
    setCarList: React.Dispatch<React.SetStateAction<TCar[]>>;
    carSelected: TCar | undefined;
    setCarSelected: React.Dispatch<React.SetStateAction<TCar | undefined>>;
    addLicensePlateForm: FormInstance<any>;
    selectLicensePlateForm: FormInstance<any>;
};
export const AddReceiptsDataContext = React.createContext<TAddReceiptsData>(
    {} as TAddReceiptsData
);
export const useAddReceipts = () => React.useContext(AddReceiptsDataContext);

const DocumentsIcon = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path
            d="M8 2C6.9 2 6 2.9 6 4V16C6 17.1 6.9 18 8 18H20C21.1 18 22 17.1 22 16V6L16 2H8ZM8 4H15V7H20V16H8V4ZM10 9V11H18V9H10ZM10 12V14H16V12H10Z"
            fill="#7357FF"
        />
    </svg>
);

const InfoIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        className="shrink-0 mt-0.5"
    >
        <path
            d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z"
            fill="#7357FF"
        />
    </svg>
);

export const AddReceiptsPage: React.FC = () => {
    const dispatch = useDispatch();
    const { showError, showSuccess } = useNotification();
    const [carList, setCarList] = React.useState<TCar[]>([]);
    const [carSelected, setCarSelected] = React.useState<TCar | undefined>();
    const [addLicensePlateForm] = useForm();
    const [selectLicensePlateForm] = useForm();

    const webcamRef = React.useRef<Webcam>(null);
    const size = useScreenSize();
    const mobileMode = size === 'xs' || size === 'sm';
    const [openCamera, setOpenCamera] = React.useState(false);
    const [imageList, setImageList] = React.useState<string[]>([]);
    const [currentFileList, setCurrentFileList] = React.useState<File[]>([]);
    const [hasImageCompression, setHasImageCompression] = React.useState(false);

    const onCompletedCapture = (image: string, imageFile: File) => {
        setImageList(pre => [...pre, image]);
        setCurrentFileList(prev => [...prev, imageFile]);
        setOpenCamera(false);
    };

    const progressInfosRef = React.useRef<any>(null);

    const uploadFiles = async () => {
        try {
            if (currentFileList != null && currentFileList.length > 0) {
                const files = Array.from(currentFileList);
                const _progressInfos = files.map(file => ({
                    percentage: 0,
                    fileName: file.name,
                }));
                progressInfosRef.current = _progressInfos;
                const result = (await Promise.all(
                    files.map(image =>
                        ApiInstance.uploadImages(
                            carSelected?.licensePlate || '',
                            image,
                            () => {}
                        )
                    )
                )) as IFile[];
                if (result) {
                    showSuccess('Success', 'Upload successfully');
                    setImageList([]);
                    setCurrentFileList([]);
                }
            }
        } catch (error: any) {
            const message = error?.response?.data?.message || error?.message;
            showError('Error', message || 'Please fill in all required fields');
        }
    };

    const onSelectedImage = (image: File) => {
        try {
            if (image instanceof File) {
                setCurrentFileList(pre => [...pre, image]);
                const reader = new FileReader();
                reader.readAsDataURL(image);
                reader.onloadend = () => {
                    if (reader.result) {
                        const newImage = reader.result as string;
                        setImageList(pre => [...pre, newImage]);
                    }
                };
            }
        } catch (error) {
            // TODO: handle error
        }
    };

    const onSubmitForm = React.useCallback(async () => {
        try {
            if (!carSelected) {
                showError('Error', 'Please select a car');
                return;
            }
            if (currentFileList.length === 0) {
                showError('Error', 'Please select at least one image');
                return;
            }
            dispatch(setLoading(true));
            await uploadFiles();
            dispatch(setLoading(false));
        } catch (error) {
            showError('Error', 'Please fill in all required fields');
        } finally {
            dispatch(setLoading(false));
        }
    }, [dispatch, carSelected, currentFileList]);

    return (
        <AddReceiptsDataContext.Provider
            value={{
                carList,
                setCarList,
                carSelected,
                setCarSelected,
                addLicensePlateForm,
                selectLicensePlateForm,
            }}
        >
            <div className="flex flex-col w-full max-w-[640px] pb-6">
                <div className="bg-white rounded-2xl border border-[var(--tc-outlines-med)] shadow-[var(--shadow-e-02)] p-5 md:p-6 flex flex-col gap-8">
                    <LicensePlateForm />

                    <div className="flex flex-col gap-4">
                        <SectionHeader
                            icon={<DocumentsIcon />}
                            title="Documents"
                            description="Upload the documents that you want to attach to this receipt."
                        />
                        <FileUpload
                            setOpenCamera={() => {
                                setHasImageCompression(true);
                                setOpenCamera(true);
                            }}
                            onSelectedImage={(file: File) => {
                                setTimeout(() => {
                                    onSelectedImage(file);
                                }, 1);
                            }}
                        />
                        <div className="flex items-start gap-2 rounded-lg bg-[var(--tc-primary-p-50)] px-3 py-2.5">
                            <InfoIcon />
                            <span className="text-sm text-med-em leading-snug">
                                You can upload multiple images.
                            </span>
                        </div>
                        {imageList?.length > 0 && (
                            <div className="flex flex-wrap gap-3 p-4 w-full bg-[var(--tc-gray-g-25)] border border-[var(--tc-outlines-med)] rounded-xl">
                                {imageList.map((image, index) => (
                                    <div
                                        key={index}
                                        className="relative rounded-lg"
                                    >
                                        <Image
                                            width="72px"
                                            height="72px"
                                            src={image}
                                            style={{
                                                objectFit: 'cover',
                                                borderRadius: '8px',
                                            }}
                                            alt={`image-${index}`}
                                        />
                                        <button
                                            type="button"
                                            aria-label="Remove image"
                                            className="flex items-center justify-center absolute -top-2 -right-2 cursor-pointer bg-[var(--system-error-500)] h-6 w-6 rounded-full border-0 p-0"
                                            onClick={() => {
                                                setImageList(
                                                    imageList.filter(
                                                        (_, i) => i !== index
                                                    )
                                                );
                                                setCurrentFileList(
                                                    currentFileList.filter(
                                                        (_, i) => i !== index
                                                    )
                                                );
                                            }}
                                        >
                                            <ICClose
                                                width={14}
                                                height={14}
                                                fill="white"
                                            />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <AutoTrackButton
                        type="primary"
                        htmlType="button"
                        btnSize="md"
                        classess="w-full !h-12 !rounded-xl"
                        btnType="primary"
                        onClick={onSubmitForm}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden
                            >
                                <path
                                    d="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3ZM19 19H5V5H16.17L19 7.83V19ZM12 12C10.34 12 9 13.34 9 15C9 16.66 10.34 18 12 18C13.66 18 15 16.66 15 15C15 13.34 13.66 12 12 12ZM6 6H15V10H6V6Z"
                                    fill="white"
                                />
                            </svg>
                            Save
                        </div>
                    </AutoTrackButton>
                </div>

                <DrawerBottom
                    title="Take a photo"
                    onClose={() => setOpenCamera(false)}
                    open={openCamera}
                    destroyOnClose
                    zIndex={1000}
                    width="100%"
                    height="100%"
                    placement="bottom"
                >
                    <div className="flex h-full flex-col justify-between w-full gap-3">
                        <div className="flex w-full h-full mt-[0]">
                            <CameraControl
                                webcamRef={webcamRef}
                                isMobile={mobileMode}
                                hasImageCompression={hasImageCompression}
                                isShowScannerOverlay={false}
                                scannerType={ScannerType.PLATE}
                                onCompletedCapture={onCompletedCapture}
                            />
                        </div>
                    </div>
                </DrawerBottom>
            </div>
        </AddReceiptsDataContext.Provider>
    );
};
